import axios from 'axios';
import { API_URL } from '../config/constants';

/**
 * Service pour récupérer et manipuler le profil utilisateur complet
 * avec ses rôles et permissions
 */
const completeUserService = {
    /**
     * Récupère le profil complet de l'utilisateur connecté
     * @param {string} token - Le token d'authentification
     * @returns {Promise<Object>} Le profil utilisateur complet avec rôles et permissions
     */
    getCompleteProfile: async (token) => {
        if (!token) {
            throw new Error('No authentication token found');
        }

        try {
            const response = await axios.get(`${API_URL}/auth/me/complete`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data.user;
        } catch (error) {
            console.error('Error fetching complete user profile:', error);
            throw error;
        }
    },

    /**
     * Vérifie si l'utilisateur a un rôle spécifique
     * @param {Object} user - L'objet utilisateur complet
     * @param {string} role - Le rôle à vérifier
     * @returns {boolean} True si l'utilisateur a le rôle, false sinon
     */
    hasRole: (user, role) => {
        if (!user || !user.roles || !Array.isArray(user.roles)) {
            return false;
        }
        return user.roles.includes(role);
    },

    /**
     * Vérifie si l'utilisateur a au moins un des rôles spécifiés
     * @param {Object} user - L'objet utilisateur complet
     * @param {Array<string>} roles - Les rôles à vérifier
     * @returns {boolean} True si l'utilisateur a au moins un des rôles, false sinon
     */
    hasAnyRole: (user, roles) => {
        if (!user || !user.roles || !Array.isArray(user.roles) || !Array.isArray(roles)) {
            return false;
        }
        return user.roles.some(role => roles.includes(role));
    },

    /**
     * Vérifie si l'utilisateur a une permission spécifique
     * @param {Object} user - L'objet utilisateur complet
     * @param {string} permission - La permission à vérifier
     * @returns {boolean} True si l'utilisateur a la permission, false sinon
     */
    hasPermission: (user, permission) => {
        if (!user || !user.permissions || !Array.isArray(user.permissions)) {
            return false;
        }
        return user.permissions.includes(permission);
    },

    /**
     * Vérifie si l'utilisateur a au moins une des permissions spécifiées
     * @param {Object} user - L'objet utilisateur complet
     * @param {Array<string>} permissions - Les permissions à vérifier
     * @returns {boolean} True si l'utilisateur a au moins une des permissions, false sinon
     */
    hasAnyPermission: (user, permissions) => {
        if (!user || !user.permissions || !Array.isArray(user.permissions) || !Array.isArray(permissions)) {
            return false;
        }
        return user.permissions.some(perm => permissions.includes(perm));
    }
};

export default completeUserService;